import { normalizeRecommendedCoverRefs } from '@/lib/blogRecommendations';
import { coverDiscountDateForDb, normalizeDiscountPercent } from '@/lib/coverDiscount';
import { coverNewDateForDb } from '@/lib/coverNewBadge';

/** Стойност за шрифт се счита „зададена" само ако е непразен string и не е 'inherit'. */
function firstRealFontFamily(value) {
  if (typeof value !== 'string') {
    return undefined;
  }
  const v = value.trim();
  return v && v !== 'inherit' ? v : undefined;
}

/**
 * Базов шрифт на корицата — ЕДНА верига, ползвана и от едитора (BookCover), и от serializer-а
 * (PDF/JSON), за да съвпадат измерване, рендер и експорт 1:1. Приоритет: template.fontFamily
 * (напр. Oddval) → categoryData.fontFamily → 'Arial, sans-serif'.
 * @param {{fontFamily?: string}|null|undefined} template
 * @param {{fontFamily?: string}|null|undefined} categoryData
 * @returns {string}
 */
export function resolveCoverBaseFontFamily(template, categoryData) {
  return (
    firstRealFontFamily(template?.fontFamily) ||
    firstRealFontFamily(categoryData?.fontFamily) ||
    'Arial, sans-serif'
  );
}

/**
 * PDF рендерът (loadCustomFont / CUSTOM_FONT_WEIGHTS) и fontsource id-то искат ЧИСТОТО име,
 * иначе локалният Oddval не се намира и шрифтът пада на Helvetica.
 * @param {string} fontFamily
 * @returns {string}
 */
export function coverFontPrimaryName(fontFamily) {
  return String(fontFamily || '')
    .split(',')[0]
    .replace(/["']/g, '')
    .trim();
}

/**
 * Нормализация за запис в БД:
 * - `undefined` — полето не е подадено (PATCH може да пропусне промяна).
 * - `null` — остава null („не е зададено“ → на сайта се показва текущата година).
 * - празно след trim — празен низ `''` („изрично без година“ за тази корица).
 * @param {unknown} value
 * @returns {string | null | undefined}
 */
export function normalizeYearValueForDb(value) {
  if (value === undefined) {
    return undefined;
  }
  if (value === null) {
    return null;
  }
  const s = String(value).trim();
  return s === '' ? '' : s;
}

/**
 * Нормализация за запис в БД на „изчистваемо“ текстово поле (заглавие на корица).
 * Същата семантика като {@link normalizeYearValueForDb}:
 * - `undefined` — полето не е подадено (PATCH пропуска промяна).
 * - `null` — остава null („не е зададено“ → на сайта се показва името на дестинацията).
 * - празно след trim — празен низ `''` („изрично без заглавие“ за тази корица).
 * @param {unknown} value
 * @returns {string | null | undefined}
 */
export function normalizeClearableTextForDb(value) {
  if (value === undefined) {
    return undefined;
  }
  if (value === null) {
    return null;
  }
  const s = String(value).trim();
  return s === '' ? '' : s;
}

/**
 * Избор на заглавие на корицата по ред източници. Огледало на
 * {@link pickCoverYearFromSources}, но дефолтът е КОНТЕКСТЕН (име на дестинация),
 * затова при липса на стойност връща sentinel `null` (извикващият слага destName):
 * - `null`/`undefined` източник = пропусни;
 * - празен низ = `''` („изрично без заглавие“);
 * - иначе първият непразен trim-нат низ;
 * - няма дефинирана стойност → `null` (извикващият решава дефолта).
 * @param {...unknown} sources
 * @returns {string | null}
 */
export function pickCoverTitleFromSources(...sources) {
  for (const v of sources) {
    if (v === undefined || v === null) {
      continue;
    }
    const s = String(v).trim();
    if (s === '') {
      return '';
    }
    return s;
  }
  return null;
}

/**
 * Ред източници: `null`/`undefined` = пропусни; празен низ = без година;
 * иначе първият непразен низ; няма такъв → текущата календарна година.
 * Не подавай друга корица от същата дестинация (напр. `templates[0].coverYear`) —
 * изчистена година там не трябва да крие годината на корици с `null` в БД.
 * @param {...unknown} sources
 * @returns {string}
 */
export function pickCoverYearFromSources(...sources) {
  for (const v of sources) {
    if (v === undefined) {
      continue;
    }
    if (v === null) {
      continue;
    }
    const s = String(v).trim();
    if (s === '') {
      return '';
    }
    return s;
  }
  return String(new Date().getFullYear());
}

/**
 * Година на гърба/задната корица: ако в шаблона няма `backYear`, следва показаната предна година.
 * @param {unknown} backRaw
 * @param {string} coverDisplayYear — вече от {@link pickCoverYearFromSources}
 * @returns {string}
 */
export function pickBackYearFromTemplate(backRaw, coverDisplayYear) {
  if (backRaw === undefined || backRaw === null) {
    return coverDisplayYear;
  }
  const s = String(backRaw).trim();
  if (s === '') {
    return '';
  }
  return s;
}

/**
 * Полетата на „NEW" тага за Prisma. Изнесено, защото същите три полета се пишат
 * и от route-овете с ръчни списъци (POST /api/destinations и PUT на дестинация).
 *
 * Периодът се чисти, когато тагът е изключен — иначе изключена корица си пази
 * стари дати и при следващо включване тагът мълчаливо не се появява.
 *
 * @param {Record<string, unknown>} template
 * @returns {{ isNew: boolean, newFrom: Date | null, newUntil: Date | null }}
 */
export function coverNewBadgeToData(template) {
  const isNew = template?.isNew === true || template?.isNew === 'true';
  return {
    isNew,
    newFrom: isNew ? coverNewDateForDb(template?.newFrom) : null,
    newUntil: isNew ? coverNewDateForDb(template?.newUntil) : null,
  };
}

/**
 * Полетата на намалението с таймер за Prisma. Като при „NEW" тага: изключването
 * чисти процента и периода.
 *
 * Без валиден процент намалението се записва като ИЗКЛЮЧЕНО — „намалена“ корица
 * с празен процент няма смисъл, а на витрината би излязъл бадж „−0%“.
 *
 * @param {Record<string, unknown>} template
 * @returns {{ isDiscounted: boolean, discountPercent: number | null,
 *   discountFrom: Date | null, discountUntil: Date | null }}
 */
export function coverDiscountToData(template) {
  const wanted = template?.isDiscounted === true || template?.isDiscounted === 'true';
  const percent = wanted ? normalizeDiscountPercent(template?.discountPercent) : null;
  const isDiscounted = percent != null;
  return {
    isDiscounted,
    discountPercent: percent,
    discountFrom: isDiscounted ? coverDiscountDateForDb(template?.discountFrom) : null,
    discountUntil: isDiscounted ? coverDiscountDateForDb(template?.discountUntil) : null,
  };
}

/**
 * Преобразува payload обект на темплейт към data за Prisma create/update.
 * Споделено между PATCH един темплейт и replace батч.
 */
export function templatePayloadToData(template, order) {
  return {
    // description и recommendedCoverRefs НЕ пътуват в публичната slim проекция
    // (catalogProjection.js) — клиент, зареден с орязани редове, ги праща липсващи.
    // Липсващо поле (undefined) = „не пипай" при update; иначе един „Запази" от
    // такъв клиент трие описанията на всички корици (замалко на 30.09.2026).
    // Изрично '' / null / [] си остава съзнателно изчистване.
    recommendedCoverRefs:
      template.recommendedCoverRefs === undefined
        ? undefined
        : normalizeRecommendedCoverRefs(template.recommendedCoverRefs),
    image: template.image || '',
    thumbnailSm: template.thumbnailSm || null,
    thumbnailMd: template.thumbnailMd || null,
    thumbnailLg: template.thumbnailLg || null,
    description: template.description === undefined ? undefined : template.description || '',
    keywords: template.keywords ?? null,
    // SEO (per cover) — попълвани от xlsx импорта или ръчно от админа
    seoH1: normalizeClearableTextForDb(template.seoH1),
    seoH1En: normalizeClearableTextForDb(template.seoH1En),
    seoMetaDescription: normalizeClearableTextForDb(template.seoMetaDescription),
    seoOgImage: normalizeClearableTextForDb(template.seoOgImage),
    // coverSlug: празно → null (не '', за да не чупи @@unique([destinationId, coverSlug]))
    coverSlug: template.coverSlug === undefined ? undefined : template.coverSlug?.trim() || null,
    backgroundColor: template.backgroundColor || null,
    coverBackground: template.coverBackground || null,
    textColor: template.textColor || null,
    yearColor: template.yearColor || null,
    coverTextColor: template.coverTextColor || null,
    coverYearColor: template.coverYearColor || null,
    spineTextColor: template.spineTextColor || null,
    backTextColor: template.backTextColor || null,
    backYearColor: template.backYearColor || null,
    copyrightTextColor: template.copyrightTextColor || null,
    logoType:
      template.logoType !== undefined && template.logoType !== null ? template.logoType : 'dark',
    coverTitle: normalizeClearableTextForDb(template.coverTitle),
    coverYear: normalizeYearValueForDb(template.coverYear),
    spineTitle: normalizeClearableTextForDb(template.spineTitle),
    backTitle: normalizeClearableTextForDb(template.backTitle),
    backYear: normalizeYearValueForDb(template.backYear),
    fontFamily: template.fontFamily || null,
    titleFontFamily: template.titleFontFamily || null,
    yearFontFamily: template.yearFontFamily || null,
    coverTitleSize:
      template.coverTitleSize !== undefined &&
      template.coverTitleSize !== null &&
      template.coverTitleSize !== ''
        ? parseInt(template.coverTitleSize, 10)
        : null,
    coverYearSize:
      template.coverYearSize !== undefined &&
      template.coverYearSize !== null &&
      template.coverYearSize !== ''
        ? parseInt(template.coverYearSize, 10)
        : null,
    spineTitleSize:
      template.spineTitleSize !== undefined &&
      template.spineTitleSize !== null &&
      template.spineTitleSize !== ''
        ? parseInt(template.spineTitleSize, 10)
        : null,
    backTitleSize:
      template.backTitleSize !== undefined &&
      template.backTitleSize !== null &&
      template.backTitleSize !== ''
        ? parseInt(template.backTitleSize, 10)
        : null,
    backYearSize:
      template.backYearSize !== undefined &&
      template.backYearSize !== null &&
      template.backYearSize !== ''
        ? parseInt(template.backYearSize, 10)
        : null,
    coverTitleX:
      template.coverTitleX !== undefined &&
      template.coverTitleX !== null &&
      template.coverTitleX !== ''
        ? parseInt(template.coverTitleX, 10)
        : null,
    coverTitleY:
      template.coverTitleY !== undefined &&
      template.coverTitleY !== null &&
      template.coverTitleY !== ''
        ? parseInt(template.coverTitleY, 10)
        : null,
    coverTitleLineHeight:
      template.coverTitleLineHeight !== undefined &&
      template.coverTitleLineHeight !== null &&
      template.coverTitleLineHeight !== ''
        ? parseFloat(template.coverTitleLineHeight)
        : null,
    coverTitleAlign: template.coverTitleAlign || null,
    coverYearX:
      template.coverYearX !== undefined &&
      template.coverYearX !== null &&
      template.coverYearX !== ''
        ? parseInt(template.coverYearX, 10)
        : null,
    coverYearY:
      template.coverYearY !== undefined &&
      template.coverYearY !== null &&
      template.coverYearY !== ''
        ? parseInt(template.coverYearY, 10)
        : null,
    objectFit: template.objectFit || 'cover',
    imageWidth: template.imageWidth || '100%',
    yearTextAlign: template.yearTextAlign || 'center',
    ...coverNewBadgeToData(template),
    ...coverDiscountToData(template),
    order,
    isActive: true,
  };
}
